import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import React, { useEffect, useRef, useState } from "react";

import styles from "./Ripple.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";

interface Props {
  duration?: number;
  className?: string;
  color?: "darkBlack" | "purple" | "white" | "";
  onClick?: (event: any) => void;
  containerClass?: string;
  dataTestId?: string;
}

const useDebouncedRippleCleanUp = (
  rippleCount: number,
  duration: any,
  cleanUpFunction: { (): void; (): void },
) => {
  useEffect(() => {
    let bounce: any = null;
    if (rippleCount > 0) {
      clearTimeout(bounce);
      bounce = setTimeout(() => {
        cleanUpFunction();
      }, duration * 4);
    }

    return clearTimeout(bounce);
  }, [rippleCount, duration, cleanUpFunction]);
};

const Ripple: React.FC<Props> = (props) => {
  const { duration, className, color, onClick, containerClass, dataTestId = "" } = props;
  const [rippleArray, setRippleArray] = useState<any>([]);
  const isClickAllowed = useRef(true);

  useDebouncedRippleCleanUp(rippleArray.length, duration, () => {
    setRippleArray([]);
  });

  const addRipple = (event: { currentTarget: any; clientX: number; clientY: number }) => {
    const rippleContainer = event.currentTarget;
    const buttonBoundingClient = rippleContainer.getBoundingClientRect();
    const size = Math.max(rippleContainer.clientWidth, rippleContainer.clientHeight);
    const radius = size / 2;
    const x = event.clientX - buttonBoundingClient.left - radius;
    const y = event.clientY - buttonBoundingClient.top - radius;
    const newRippleArray = {
      x,
      y,
      size,
    };
    setRippleArray([...rippleArray, newRippleArray]);
  };

  const handleOnClick = (e: any) => {
    e.stopPropagation();
    e.preventDefault();
    if (isClickAllowed.current && onClick !== undefined) {
      isClickAllowed.current = false;
      setTimeout(() => {
        if (onClick) {
          onClick(e);
          isClickAllowed.current = true;
        }
      }, 250);
    }
  };

  const onKeyDown = (event: any) => {
    if (event.key === " ") {
      handleOnClick(event);
    }
  };

  return (
    <div
      role="button"
      className={formatClassNames(styles.container, containerClass)}
      onClick={handleOnClick}
      onMouseDown={addRipple}
      onKeyDown={onKeyDown}
      tabIndex={0}
      data-testid={`${dataTestId}tab`}
    >
      {rippleArray &&
        rippleArray.length > 0 &&
        rippleArray.map((ripple: any, index: any) => (
          <span
            key={`span${index}`}
            style={{
              top: ripple.y,
              left: ripple.x,
              width: ripple.size,
              height: ripple.size,
              animationDuration: `${duration || 450}ms`,
            }}
            className={formatClassNames(styles.rippleSpan, className, color && styles[color])}
          />
        ))}
    </div>
  );
};

Ripple.defaultProps = {
  duration: 450,
  className: "",
  color: "",
  containerClass: "",
  onClick: undefined,
};

export default wrapperHOC(Ripple, {
  componentName: "Ripple-Global",
  showForChina: true,
});
